import { useState, useEffect, useRef } from 'react';
import { useNoteEditorBuffer } from '@/hooks/lesson/useNoteEditorBuffer';
import { useSlashMenu } from '@/hooks/lesson/useSlashMenu';
import { SlashMenu } from './SlashMenu';
import { NoteSelector } from './NoteSelector';
import { CheckCircle2, Loader2, Plus, StickyNote } from 'lucide-react';
import ReactQuill from 'react-quill';
/**
 * `ReactQuill`'s own `onChange` signature, derived rather than hand-written.
 *
 * Both editors below used `(content: string, delta: any, source: string,
 * editor: any)`. The `any`s were load-bearing in exactly one place — `source`
 * is `Sources` (`'user' | 'api' | 'silent'`), and typing it as `string` is what
 * let the critical `source === 'user'` guard below be written against a value
 * the compiler could not check. Deriving the type from the prop keeps that
 * guard honest and keeps `editor` structurally compatible with
 * `SlashDetectionEditor`.
 */
type QuillChange = NonNullable<ReactQuill['props']['onChange']>;
import 'react-quill/dist/quill.snow.css';
import type { LessonStyle } from '@/types/lesson';
import { mockPersonalNotesDocument } from '@/data/fixtures';
import type { PersonalNotesDocument } from '@/data/fixtures';
import { useAutoSave } from '@/hooks/ui/useAutoSave';
import { ComingSoon } from '@/components/core/common/feedback/ComingSoon';
import { PanelLoading } from '@/components/features/learning/common/PanelLoading';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  useNotesList,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from '@/hooks/lesson/useNotes';
import type { NoteItem } from '@/lib/api-client';
import { useActivityTracking } from '@/hooks/useActivityTracking';

interface NotesViewerProps {
  lessonId?: string;
  lessonStyle: LessonStyle;
  onNotesUpdate?: (aiNotes: number, personalNotes: number) => void;
}

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'blockquote',
  'code-block',
  'link',
];

const quillModules = { toolbar: false };

// ============================================================================
// API NOTES EDITOR (top-bar selector + full-width editor)
// ============================================================================

function ApiNotesEditor({ lessonId }: { lessonId: string }) {
  const { data: notesData, isLoading } = useNotesList(lessonId);
  const createNote = useCreateNote(lessonId);
  const updateNote = useUpdateNote(lessonId);
  const deleteNote = useDeleteNote(lessonId);

  const notes = notesData?.notes ?? [];

  // Selection + editor buffer (and their two effects) live in
  // `useNoteEditorBuffer`, which is covered by renderHook tests exercising the
  // real effect cycle — this component has no render tests of its own, so that
  // is where the behaviour is pinned. Called here, after the mutation hooks,
  // because it needs `updateNote.isPending`.
  const {
    selectedNoteId,
    selectedNote,
    selectNote,
    clearSelection,
    editorContent,
    setEditorContent,
    editorTitle,
    setEditorTitle,
  } = useNoteEditorBuffer<NoteItem>({
    notes,
    isSaving: updateNote.isPending,
  });

  const quillRef = useRef<ReactQuill>(null);
  const slashMenu = useSlashMenu(quillRef);

  // The LATEST title, for `onSave` to read 2000ms later.
  //
  // Reading `editorTitle` directly there dropped the last character of every
  // title edit, silently, while the chip said "Saved". `handleTitleChange` runs
  // during the keystroke's event, BEFORE React has re-rendered with the new
  // value — so the `triggerSave` it calls is the previous render's, and the
  // `onSave` that closure captured sees the title as it was one keystroke ago.
  // Every earlier timeout is cleared by the next keystroke, so the one that
  // survives is the last one, carrying the second-to-last value: typing
  // "Light reactions" saved "Light reaction".
  //
  // `content` never had this problem because it is passed by value from the
  // event. A ref is the local fix; the general one is for `useAutoSave` to hold
  // the latest `onSave` itself, which is a change to a hook with its own tests
  // and other callers.
  const editorTitleRef = useRef(editorTitle);
  editorTitleRef.current = editorTitle;

  const { saveStatus, lastSaved, triggerSave } = useAutoSave({
    delay: 2000,
    onSave: async (content: string) => {
      if (!selectedNoteId) return;
      await updateNote.mutateAsync({
        noteId: selectedNoteId,
        data: { content, title: editorTitleRef.current },
      });
    },
  });

  const handleContentChange: QuillChange = (content, delta, source, editor) => {
    setEditorContent(content);
    // Only schedule a save for genuine user typing. Quill also fires
    // onChange with source === 'api' / 'silent' when the `value` prop is
    // updated programmatically (e.g. when `selectNote` swaps notes after
    // a list refetch). Saving on those events can write the freshly-loaded
    // content of one note back into a stale `selectedNoteId` closure,
    // making it look like "save to notes" wrote into an existing empty note.
    if (source === 'user') {
      triggerSave(content);
    }
    slashMenu.handleSlashDetection(content, delta, source, editor);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditorTitle(e.target.value);
    triggerSave(editorContent);
  };

  const handleCreateNote = async () => {
    const result = await createNote.mutateAsync({ title: 'Untitled' });
    selectNote(result);
  };

  const handleDeleteNote = async (noteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteNote.mutateAsync(noteId);
    if (selectedNoteId === noteId) {
      const remaining = notes.filter(n => n.note_id !== noteId);
      if (remaining.length > 0) {
        selectNote(remaining[0]);
      } else {
        clearSelection();
      }
    }
  };

  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <PanelLoading
        icon={StickyNote}
        title='Loading notes'
        description='Fetching your notes for this lesson.'
      />
    );
  }

  // Empty state - matches OnDemandWrapper "generate" CTA pattern used by
  // Quiz / Glossary / Extras: fade-up animation + neutral icon + black button.
  // Icon matches the Notes tab nav icon (`StickyNote`) for consistency.
  if (notes.length === 0) {
    return (
      <div className='flex h-full animate-fade-in flex-col items-center justify-center px-4 py-16'>
        <div className='mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
          <StickyNote className='h-7 w-7 text-brand-500/80 dark:text-primary' />
        </div>
        <h3 className='mb-2 text-lg font-medium text-foreground'>
          Personal Notes
        </h3>
        <p className='mb-6 max-w-sm text-center text-sm text-muted-foreground'>
          Capture key insights, questions, and your own thinking as you work
          through the lesson.
        </p>
        <button
          type='button'
          onClick={handleCreateNote}
          disabled={createNote.isPending}
          className='inline-flex items-center gap-2 rounded-md bg-black px-4 py-2 text-sm font-medium text-white shadow-sm transition-shadow hover:bg-black/90 hover:shadow-md disabled:opacity-50 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 max-sm:min-h-11'
        >
          {createNote.isPending ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Plus className='h-4 w-4' />
          )}
          {createNote.isPending ? 'Creating…' : 'Create your first note'}
        </button>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      {/* Top bar: note selector + new + save status */}
      <div className='flex flex-shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3'>
        <NoteSelector
          notes={notes}
          selectedNoteId={selectedNoteId}
          onSelect={selectNote}
          onDelete={handleDeleteNote}
        />
        <div className='flex items-center gap-2'>
          {saveStatus === 'saving' && (
            <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <Loader2 className='h-3 w-3 animate-spin' />
              Saving…
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              <CheckCircle2 className='h-3 w-3 text-green-500' />
              Saved {formatLastSaved(lastSaved)}
            </span>
          )}
          {saveStatus === 'error' && (
            <span className='text-xs text-destructive-emphasis'>
              Failed to save
            </span>
          )}
          <button
            type='button'
            onClick={handleCreateNote}
            disabled={createNote.isPending}
            // Icon-only below `sm`, so it carries its own name, and is a 44px
            // square there.
            className='inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-all duration-200 hover:bg-muted hover:text-foreground disabled:opacity-50 max-sm:h-11 max-sm:w-11 max-sm:flex-shrink-0 max-sm:justify-center max-sm:px-0'
            aria-label='New note'
            title='Create new note'
          >
            {createNote.isPending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Plus className='h-4 w-4' />
            )}
            <span className='hidden sm:inline'>New</span>
          </button>
        </div>
      </div>

      {/* Editor - full width with comfortable max width */}
      <div className='relative flex-1 overflow-y-auto'>
        {selectedNote && (
          <div className='mx-auto max-w-3xl px-5 py-6 sm:px-10 sm:py-8 lg:px-12'>
            <input
              type='text'
              value={editorTitle}
              onChange={handleTitleChange}
              placeholder='Untitled'
              className='mb-4 w-full border-none bg-transparent text-2xl font-bold text-foreground outline-none placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:placeholder:text-muted-foreground max-sm:min-h-11'
            />
            <ReactQuill
              ref={quillRef}
              value={editorContent}
              onChange={handleContentChange}
              placeholder="Type '/' for commands…"
              theme='snow'
              modules={quillModules}
              formats={quillFormats}
              style={{ minHeight: '400px', border: 'none' }}
              className='notion-editor'
            />
            <SlashMenu {...slashMenu} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MOCK NOTES EDITOR (single-document fallback for mock data mode)
// ============================================================================

function MockNotesEditor({
  onContentChange,
}: {
  onContentChange?: (hasContent: boolean) => void;
}) {
  const [notesDocument, setNotesDocument] = useState<PersonalNotesDocument>(
    mockPersonalNotesDocument
  );
  const [editorContent, setEditorContent] = useState(
    mockPersonalNotesDocument.content
  );
  const quillRef = useRef<ReactQuill>(null);
  const slashMenu = useSlashMenu(quillRef);

  const { saveStatus, lastSaved, triggerSave } = useAutoSave({
    delay: 2000,
    onSave: async (content: string) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      setNotesDocument(prev => ({ ...prev, content, lastSaved: new Date() }));
    },
  });

  const handleContentChange: QuillChange = (content, delta, source, editor) => {
    setEditorContent(content);
    if (source === 'user') {
      triggerSave(content);
    }
    slashMenu.handleSlashDetection(content, delta, source, editor);
  };

  // `onContentChange` is deliberately NOT a dependency.
  //
  // The call site passes an inline arrow, so its identity changes on every
  // render. With it in the deps this effect re-fired after every render,
  // called back into `LessonViewer`, which set state, which re-rendered, which
  // produced a new arrow — an infinite loop that pegged the tab the moment the
  // Notes tab was opened on a real lesson page. It did not reproduce in the
  // isolated `NotesViewer` story because there is no parent state there to
  // change.
  //
  // The effect is about the CONTENT changing; the callback is just where the
  // result is delivered. Keeping it out of the deps is the standard shape for
  // a callback prop, and the latest one is always in scope anyway.
  useEffect(() => {
    const hasContent = editorContent.replace(/<[^>]*>/g, '').trim().length > 0;
    onContentChange?.(hasContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorContent]);

  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className='flex h-full flex-col'>
      <div className='flex flex-shrink-0 items-center justify-end border-b border-border px-4 py-2'>
        {saveStatus === 'saving' && (
          <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Saving…
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
            <CheckCircle2 className='h-3 w-3 text-green-500' />
            Saved {formatLastSaved(lastSaved || notesDocument.lastSaved)}
          </span>
        )}
        {saveStatus === 'error' && (
          <span className='text-xs text-destructive-emphasis'>
            Failed to save
          </span>
        )}
        {saveStatus === 'idle' && lastSaved && (
          <span className='text-xs text-muted-foreground'>
            Last saved {formatLastSaved(lastSaved)}
          </span>
        )}
      </div>
      <div className='relative flex-1 overflow-y-auto'>
        <div className='mx-auto max-w-3xl px-5 py-6 sm:px-10 sm:py-8 lg:px-12'>
          <ReactQuill
            ref={quillRef}
            value={editorContent}
            onChange={handleContentChange}
            placeholder="Type '/' for commands…"
            theme='snow'
            modules={quillModules}
            formats={quillFormats}
            style={{ minHeight: '500px', border: 'none' }}
            className='notion-editor'
          />
          <SlashMenu {...slashMenu} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function NotesViewer({
  lessonId: _lessonId = 'lesson-1',
  lessonStyle: __,
  onNotesUpdate,
}: NotesViewerProps) {
  const { isApi, isEmpty } = useDataMode();

  // Mount/unmount activity tracking for the Notes sub-tab. We emit even
  // in mock/empty modes (the hook silently no-ops when unauthenticated)
  // so analytics capture is consistent across data modes when running
  // against a real backend.
  useActivityTracking({ contentType: 'notes', lessonId: _lessonId });

  if (isApi) {
    return <ApiNotesEditor lessonId={_lessonId} />;
  }
  if (isEmpty) {
    return (
      <ComingSoon
        icon={StickyNote}
        title='Personal Note-Taking'
        description='Take notes with a powerful editor featuring slash commands and formatting options.'
      />
    );
  }
  return (
    <MockNotesEditor
      onContentChange={hasContent => onNotesUpdate?.(0, hasContent ? 1 : 0)}
    />
  );
}
